/**
 * RosterTable: the Directory's roster, sortable and grouped by unit when
 * that is the active sort. Wide (900px and up) renders the full table;
 * narrow renders the same rows as two-line list items (name and role;
 * unit and phone) behind the same filters, opening the same drawer.
 *
 * A person with the private-contact tier served (home address or personal
 * phone present) carries a small lock chip that reveals the pair on
 * click/tap -- the same server-decided gate api/_shared/teamDirectory.ts
 * projects by: this table never decides visibility itself, it only
 * renders what arrived.
 */

import { Fragment, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, Lock, Phone } from 'lucide-react';
import {
  ANNIVERSARY_MARK,
  BIRTHDAY_MARK,
  anniversaryLine,
  birthdayLabel,
  groupSortedRosterByUnit,
  isAnniversaryMonth,
  type RosterRowGroup,
  roleLine,
  type RosterSort,
  type RosterSortField,
  type TeamDirectoryPerson,
} from '@/lib/teamDirectory';
import { formatPhoneDisplay } from '@/lib/phone';
import { Button } from '@/components/primitives/Button';
import { TeamPhoto } from './TeamPhoto';

const HEAD_ROW = 'text-left text-label font-semibold uppercase tracking-label text-sage-400';
/**
 * The table owns its own scroll box (the roster-table-scroll div below), so
 * the header sticks at ITS top, not at an offset borrowed from the AppShell header
 * (that offset only works when the page itself is the scrolling ancestor,
 * as it is for the Invoices.tsx RAIL -- a table with its own bounded,
 * scrollable box is a different containing block).
 */
const STICKY_HEAD = 'sticky top-0 z-10 bg-paper';

/**
 * Column widths (px), fed to <colgroup> under table-layout: fixed so a long
 * value (a wrapping work phone, an inconsistent email) cannot grow a row
 * past its bounded height -- it scrolls within its own cell instead. The
 * name column is the one exception allowed to wrap, up to two lines. The
 * unit column itself drops out of the <colgroup>, the header and every row
 * while the table is grouped by unit (RosterTable's showUnitColumn) -- the
 * group header already names it -- so the other columns' widths never need
 * to move to fill the gap.
 */
const COLUMN_WIDTHS = {
  avatar: 36,
  name: 220,
  role: 130,
  unit: 170,
  phone: 140,
  email: 260,
  birthday: 120,
  since: 200,
  chip: 36,
} as const;

/** No-wrap treatment for the columns that must never wrap onto a second line. */
const NOWRAP_CELL = 'whitespace-nowrap';
/** Every data-row <td> shares this padding, sized so a single-line row (the
 *  row height driven by the sm avatar, 28px) lands at ~44px at rest. */
const CELL_PADDING = 'py-2';

function SortArrow({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }): ReactNode {
  if (!active) return null;
  const Icon = dir === 'asc' ? ChevronUp : ChevronDown;
  return <Icon size={12} aria-hidden="true" className="inline-block ml-0.5 align-text-bottom" />;
}

function SortHeader({
  field,
  label,
  sort,
  onSort,
  align,
}: {
  field: RosterSortField;
  label: string;
  sort: RosterSort;
  onSort: (field: RosterSortField) => void;
  align?: 'right';
}): ReactNode {
  const active = sort.field === field;
  return (
    <th className={`py-1 pr-3 font-semibold ${align === 'right' ? 'text-right' : ''}`} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onSort(field)}
        data-testid={`roster-sort-${field}`}
        className="!min-h-0 !px-1.5 !py-0.5 text-label font-semibold uppercase tracking-label !text-sage-400 hover:!text-sage-800"
      >
        {label}
        <SortArrow active={active} dir={sort.dir} />
      </Button>
    </th>
  );
}

function PrivateContactChip({ person }: { person: TeamDirectoryPerson }): ReactNode {
  const [open, setOpen] = useState(false);
  const has = person.home_address !== null || person.personal_phone !== null;
  if (!has) return null;
  return (
    <span className="relative inline-block">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        icon={<Lock size={12} aria-hidden="true" />}
        aria-label={`Show private contact for ${person.full_name}`}
        aria-expanded={open}
        data-testid={`private-contact-chip-${person.roster_id}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="!min-h-0 !min-w-0 h-6 w-6 !rounded-full !border-warm-400/40 !bg-sand !px-0 !py-0 !text-warm-700 hover:!bg-warm-100"
      />
      {open && (
        <span
          data-testid={`private-contact-panel-${person.roster_id}`}
          className="absolute right-0 z-20 mt-1 w-56 rounded-base border border-warm-400/40 bg-paper p-2 text-xs text-ink shadow-sh2"
        >
          <span className="mb-1 block text-label font-semibold uppercase tracking-label text-warm-700">
            Private contact
          </span>
          {person.home_address && <span className="block">{person.home_address}</span>}
          {person.personal_phone && <span className="block">{formatPhoneDisplay(person.personal_phone)}</span>}
        </span>
      )}
    </span>
  );
}

export interface UnitGroupHeaderRowProps {
  label: string;
  counties: string[];
  hint?: string;
  colSpan: number;
  /** The header row's own testid prefix ("<prefix>-unit-header-<label>"),
   *  so a table other than the roster (the merged availability table)
   *  can reuse this exact row without colliding on the roster's own
   *  testids. Defaults to 'roster' — every existing caller is unchanged. */
  testIdPrefix?: string;
}

/** The roster's own unit/office/no-unit group header row: a full-width
 *  band naming the group, its counties (when it has any) and an
 *  optional hint. Exported so another staff table can group the same
 *  way without a second copy of this row's look. */
export function UnitGroupHeaderRow({
  label,
  counties,
  hint,
  colSpan,
  testIdPrefix = 'roster',
}: UnitGroupHeaderRowProps): ReactNode {
  return (
    <tr data-testid={`${testIdPrefix}-unit-header-${label}`}>
      <td colSpan={colSpan} className="bg-sand px-2 py-1">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-sage-400">{label}</span>
        {counties.length > 0 && (
          <span className="ml-2 text-xs text-ink-soft" data-testid="roster-unit-counties">
            {counties.join(' · ')}
          </span>
        )}
        {hint && <span className="ml-2 text-xs text-ink-soft">{hint}</span>}
      </td>
    </tr>
  );
}

/** The Birthday cell's content: the cake mark plus the date, always
 *  together -- a birthday cell with nothing on file renders nothing, so
 *  the mark never appears alone. aria-label spells "Birthday" out in
 *  words; the mark itself renders as real text (BIRTHDAY_MARK), not an
 *  icon. */
function BirthdayCell({ label }: { label: string | null }): ReactNode {
  if (!label) return null;
  return <span aria-label={`Birthday ${label}`}>{BIRTHDAY_MARK} {label}</span>;
}

/** The Since cell's content: the line always renders; the party-popper
 *  mark joins it only in the person's completed-anniversary month
 *  (isAnniversaryMonth) -- a birthday every year, a workiversary once a
 *  year. */
function SinceCell({ line, celebrate }: { line: string | null; celebrate: boolean }): ReactNode {
  if (!line) return null;
  if (!celebrate) return line;
  return <span aria-label={`Work anniversary this month. ${line}`}>{ANNIVERSARY_MARK} {line}</span>;
}

interface RowProps {
  tenantSlug: string;
  person: TeamDirectoryPerson;
  today: string;
  onClick: (person: TeamDirectoryPerson) => void;
  /** False while grouped by unit: the group header above already names it,
   *  so the row does not repeat it in its own cell. */
  showUnit: boolean;
}

function WideRow({ tenantSlug, person, today, onClick, showUnit }: RowProps): ReactNode {
  const birthday = birthdayLabel(person.birthday_month, person.birthday_day);
  const anniversary = anniversaryLine(person, today);
  const celebrateAnniversary = isAnniversaryMonth(person, today);
  return (
    <tr
      data-testid={`roster-row-${person.roster_id}`}
      role="button"
      tabIndex={0}
      aria-label={`Open ${person.full_name}`}
      onClick={() => onClick(person)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(person);
        }
      }}
      className="cursor-pointer hover:bg-sand focus-visible:outline-none focus-visible:bg-sand"
    >
      <td className={`${CELL_PADDING} pr-2`}>
        <TeamPhoto tenantSlug={tenantSlug} person={person} size="sm" />
      </td>
      <td className={`${CELL_PADDING} pr-3`}>
        <span className="font-medium text-ink">{person.full_name}</span>
        {person.credentials && <span className="text-ink-soft">, {person.credentials}</span>}
      </td>
      <td className={`${CELL_PADDING} pr-3 ${NOWRAP_CELL} truncate text-ink-soft`}>{roleLine(person)}</td>
      {showUnit && (
        <td className={`${CELL_PADDING} pr-3 ${NOWRAP_CELL} truncate text-ink-soft`}>{person.unit_name ?? ''}</td>
      )}
      <td className={`${CELL_PADDING} pr-3 ${NOWRAP_CELL} text-ink-soft`}>
        {person.work_phone ? formatPhoneDisplay(person.work_phone) : '–'}
      </td>
      <td className={`${CELL_PADDING} pr-3 ${NOWRAP_CELL} truncate text-ink-soft`}>
        {person.work_email && (
          <a
            href={`mailto:${person.work_email}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sage-800 underline-offset-2 hover:underline"
          >
            {person.work_email}
          </a>
        )}
      </td>
      <td className={`${CELL_PADDING} pr-3 ${NOWRAP_CELL} tabular-nums text-ink-soft`}>
        <BirthdayCell label={birthday} />
      </td>
      <td className={`${CELL_PADDING} pr-3 ${NOWRAP_CELL} tabular-nums text-ink-soft`}>
        <SinceCell line={anniversary} celebrate={celebrateAnniversary} />
      </td>
      <td className={`${CELL_PADDING} pr-1 text-right`}>
        <PrivateContactChip person={person} />
      </td>
    </tr>
  );
}

function NarrowRow({ tenantSlug, person, onClick }: Omit<RowProps, 'today' | 'showUnit'>): ReactNode {
  return (
    <li
      data-testid={`roster-row-${person.roster_id}`}
      role="button"
      tabIndex={0}
      aria-label={`Open ${person.full_name}`}
      onClick={() => onClick(person)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(person);
        }
      }}
      className="flex cursor-pointer items-center gap-3 px-1 py-2.5 hover:bg-sand focus-visible:outline-none focus-visible:bg-sand"
    >
      <TeamPhoto tenantSlug={tenantSlug} person={person} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">
          {person.full_name}
          {person.credentials && <span className="font-normal text-ink-soft">, {person.credentials}</span>}
        </p>
        <p className="truncate text-xs text-ink-soft">{roleLine(person)}</p>
        <p className="truncate text-xs text-ink-soft">
          {person.unit_name ?? 'No unit'}
          {person.work_phone && (
            <>
              {' · '}
              <Phone size={10} className="inline-block align-text-bottom" aria-hidden="true" />{' '}
              {formatPhoneDisplay(person.work_phone)}
            </>
          )}
        </p>
      </div>
      {(person.home_address !== null || person.personal_phone !== null) && (
        <Lock
          size={14}
          className="shrink-0 text-warm-700"
          data-testid={`private-contact-chip-${person.roster_id}`}
          aria-label={`Has a private contact on file for ${person.full_name}`}
        />
      )}
    </li>
  );
}

export interface RosterTableProps {
  tenantSlug: string;
  people: readonly TeamDirectoryPerson[];
  sort: RosterSort;
  onSort: (field: RosterSortField) => void;
  countiesByUnit: Map<string, string[]>;
  onRowClick: (person: TeamDirectoryPerson) => void;
  today: string;
  isNarrow: boolean;
}

const WIDE_COLUMN_COUNT = 9;

export function RosterTable({
  tenantSlug,
  people,
  sort,
  onSort,
  countiesByUnit,
  onRowClick,
  today,
  isNarrow,
}: RosterTableProps): ReactNode {
  if (isNarrow) {
    return (
      <ul data-testid="roster-list" className="divide-y divide-sage-800/[6%]">
        {people.map((p) => (
          <NarrowRow key={p.roster_id} tenantSlug={tenantSlug} person={p} onClick={onRowClick} />
        ))}
      </ul>
    );
  }

  const groups: RosterRowGroup[] =
    sort.field === 'unit'
      ? groupSortedRosterByUnit(people)
      : [{ kind: 'unit', label: '', unitName: null, people: [...people] }];
  const showGroupHeaders = sort.field === 'unit';
  // Grouped by unit, the group header above already names it -- a per-row
  // Unit cell would only repeat that same text. Every other sort shows it.
  const showUnitColumn = !showGroupHeaders;
  const columnCount = showUnitColumn ? WIDE_COLUMN_COUNT : WIDE_COLUMN_COUNT - 1;

  return (
    // Bounded height + overflow-auto (both axes) makes this div the table's
    // OWN scroll container, so the sticky <thead> below sticks at its top
    // (top-0) as the rows scroll past it, and a viewport narrower than
    // TABLE_MIN_WIDTH scrolls horizontally instead of wrapping any column.
    <div data-testid="roster-table-scroll" className="overflow-auto max-h-[calc(100vh-280px)]">
      {/* min-w-[1312px] is COLUMN_WIDTHS' sum with the unit column
          (36+220+130+170+140+260+120+200+36); min-w-[1142px] is the same
          sum without it (170 less). Keep both in sync if a width changes. */}
      <table
        data-testid="roster-table"
        className={`${showUnitColumn ? 'min-w-[1312px]' : 'min-w-[1142px]'} table-fixed text-sm`}
      >
        <colgroup>
          <col style={{ width: COLUMN_WIDTHS.avatar }} />
          <col style={{ width: COLUMN_WIDTHS.name }} />
          <col style={{ width: COLUMN_WIDTHS.role }} />
          {showUnitColumn && <col style={{ width: COLUMN_WIDTHS.unit }} />}
          <col style={{ width: COLUMN_WIDTHS.phone }} />
          <col style={{ width: COLUMN_WIDTHS.email }} />
          <col style={{ width: COLUMN_WIDTHS.birthday }} />
          <col style={{ width: COLUMN_WIDTHS.since }} />
          <col style={{ width: COLUMN_WIDTHS.chip }} />
        </colgroup>
        <thead>
          <tr className={`${HEAD_ROW} ${STICKY_HEAD}`}>
            <th className="py-1.5 pr-2" />
            <SortHeader field="name" label="Name" sort={sort} onSort={onSort} />
            <SortHeader field="role" label="Role" sort={sort} onSort={onSort} />
            {showUnitColumn && <SortHeader field="unit" label="Unit" sort={sort} onSort={onSort} />}
            <th className="py-1.5 pr-3 font-semibold">Work phone</th>
            <th className="py-1.5 pr-3 font-semibold">Work email</th>
            <th className="py-1.5 pr-3 font-semibold">Birthday</th>
            <SortHeader field="since" label="Since" sort={sort} onSort={onSort} />
            <th className="py-1.5 pr-1" />
          </tr>
        </thead>
        <tbody className="divide-y divide-sage-800/[6%]">
          {groups.map((g, i) => (
            <Fragment key={`${g.kind}:${g.unitName ?? g.label}:${i}`}>
              {showGroupHeaders && (
                <UnitGroupHeaderRow
                  label={g.label}
                  counties={g.unitName ? (countiesByUnit.get(g.unitName) ?? []) : []}
                  hint={g.kind === 'no_unit' ? 'Set the unit on the People page.' : undefined}
                  colSpan={columnCount}
                />
              )}
              {g.people.map((p) => (
                <WideRow
                  key={p.roster_id}
                  tenantSlug={tenantSlug}
                  person={p}
                  today={today}
                  onClick={onRowClick}
                  showUnit={showUnitColumn}
                />
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
